import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { Layout } from "@/components/layout";
import { useCreateSession } from "@workspace/api-client-react";
import { useTrialExhaustedDialog } from "@/hooks/use-trial-exhausted-dialog";
import { useAuth } from "@/hooks/useAuth";
import { isTrialExhausted } from "@/lib/trial";
import { motion } from "framer-motion";
import { Link2, Type, Mic, MicOff, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { Waveform } from "@/components/waveform";
import {
  clearPendingInput,
  readPendingInput,
  writePendingInput,
  UNFOLD_FAILED_PARAM,
  type PendingInput,
} from "@/lib/pending-input";
import { getLinkPreview } from "@/lib/link-preview";
import { LinkPreviewCard } from "@/components/link-preview-card";

export default function InputScreen() {
  const [, setLocation] = useLocation();
  const { user, refetch } = useAuth();
  const [inputType, setInputType] = useState<"link" | "text">("link");
  const [inputValue, setInputValue] = useState("");

  const [submitError, setSubmitError] = useState<string | null>(null);
  const trialBlocked = isTrialExhausted(user);
  const { trialExhaustedDialog, showTrialExhaustedDialog } = useTrialExhaustedDialog();

  const { mutate: createSession, isPending } = useCreateSession({
    mutation: {
      onSuccess: (data, { data: submitted }) => {
        setSubmitError(null);
        void refetch();
        // Kept until processing finishes, so a failed unfolding can hand the input back.
        writePendingInput({ value: submitted.input, type: submitted.type as PendingInput["type"] });
        setLocation(`/processing/${data.id}`);
      },
      onError: (err: unknown) => {
        if (showTrialExhaustedDialog(err)) {
          setSubmitError(null);
          void refetch();
          return;
        }
        setSubmitError("Something went wrong. Please try again.");
      },
    },
  });

  const voice = useVoiceInput({
    onTranscript: (text) => setInputValue((p) => (p ? p + " " + text : text)),
  });

  useEffect(() => {
    const pending = readPendingInput();
    if (pending) {
      setInputType(pending.type);
      setInputValue(pending.value);
      clearPendingInput();
    }
    // Sent back here by the processing screen after the unfolding failed.
    const params = new URLSearchParams(window.location.search);
    if (params.has(UNFOLD_FAILED_PARAM)) {
      setSubmitError("We couldn't unfold this right now. Please try again in a few minutes.");
      params.delete(UNFOLD_FAILED_PARAM);
      const rest = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : ""));
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isPending || trialBlocked) return;
    if (voice.isListening) voice.stop();
    createSession({ data: { input: inputValue, type: inputType } });
  };

  const linkPreview = inputType === "link" ? getLinkPreview(inputValue) : null;

  return (
    <Layout>
      {trialExhaustedDialog}
      <div className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center px-5 py-10 relative overflow-hidden">
        {/* Soft pastel washes from the landing hero */}
        <div aria-hidden className="brand-washes absolute inset-0 pointer-events-none" />

        <div className="w-full max-w-xl relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 text-center"
          >
            <h1 className="page-title font-display text-3xl md:text-4xl mb-2">
              Let's unfold <span className="text-primary">this</span>
            </h1>
            <p className="text-base leading-6 text-muted-foreground">
              Drop a YouTube link, Instagram Reel, article, or social post.
            </p>
          </motion.div>

          {/* Rate limit banner */}
          {trialBlocked && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm text-center space-y-2"
            >
              <p>You&apos;ve used all {user?.trialEntriesLimit ?? 5} Starter Unfoldings.</p>
              <Link href="/pricing" className="inline-block font-semibold text-primary hover:underline">
                Upgrade to keep unfolding
              </Link>
            </motion.div>
          )}

          {submitError && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm text-center"
            >
              {submitError}
            </motion.div>
          )}

          {/* Input card */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.08 }}
            className="surface-card overflow-hidden"
          >
            {/* Type toggles */}
            <div className="flex items-center gap-2 p-3 border-b-[0.6px] border-foreground/5">
              <button
                type="button"
                onClick={() => { setInputType("link"); voice.isListening && voice.stop(); }}
                className={cn(
                  "flex items-center gap-1.5 h-9 px-3.5 rounded-[18px] text-sm font-medium transition-all",
                  inputType === "link"
                    ? "bg-white text-foreground shadow-[0_1px_1px_rgba(47,41,37,0.1),0_1px_1.5px_rgba(47,41,37,0.1)]"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Link2 className="w-3.5 h-3.5" /> Link
              </button>
              <button
                type="button"
                onClick={() => { setInputType("text"); voice.isListening && voice.stop(); }}
                className={cn(
                  "flex items-center gap-1.5 h-9 px-3.5 rounded-[18px] text-sm font-medium transition-all",
                  inputType === "text"
                    ? "bg-white text-foreground shadow-[0_1px_1px_rgba(47,41,37,0.1),0_1px_1.5px_rgba(47,41,37,0.1)]"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Type className="w-3.5 h-3.5" /> Text
              </button>

              {inputType === "text" && voice.isSupported && (
                <button
                  type="button"
                  onClick={voice.toggle}
                  className={cn(
                    "ml-auto flex items-center gap-1.5 h-9 px-3.5 rounded-[18px] text-sm font-medium transition-all",
                    voice.isListening
                      ? "bg-primary text-white"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {voice.isListening ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                  {voice.isListening ? "Stop" : "Speak"}
                </button>
              )}
            </div>

            {/* Waveform indicator */}
            {voice.isListening && (
              <div className="flex items-center gap-3 px-5 py-2.5 bg-primary/5 border-b border-primary/10">
                <Waveform active className="h-5" />
                <span className="text-sm text-primary/70 animate-pulse font-medium">Listening…</span>
              </div>
            )}

            {/* YouTube preview card */}
            {linkPreview && (
              <LinkPreviewCard
                preview={linkPreview}
                href={inputValue.trim()}
                onClear={() => setInputValue("")}
              />
            )}

            {/* Text area */}
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={
                inputType === "link"
                  ? "Paste a YouTube, Instagram, TikTok, or article URL…"
                  : "Write whatever's on your mind right now…"
              }
              aria-label={inputType === "link" ? "Link to unfold" : "Thought to unfold"}
              className="w-full min-h-[110px] bg-transparent p-5 text-lg leading-7 text-foreground placeholder:text-muted-foreground/60 focus:outline-none resize-none"
              disabled={isPending || trialBlocked}
            />

            {/* Submit row */}
            <div className="flex items-center justify-between p-4 border-t-[0.6px] border-foreground/5 gap-3">
              <span className="text-xs text-muted-foreground hidden sm:block">
                {inputType === "link" ? "YouTube, Instagram, TikTok, X, or any URL" : "Any thought, feeling, or idea"}
              </span>
              <button
                type="submit"
                disabled={!inputValue.trim() || isPending || trialBlocked}
                className="flex h-10 items-center gap-2 px-5 rounded-full bg-primary text-white font-medium text-sm disabled:opacity-40 transition-all hover:bg-primary/90 shadow-[0_6px_10px_rgba(110,83,172,0.45)] ml-auto"
              >
                {isPending ? "Starting session…" : "Unfold this"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.form>

          {/* Library link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-10 flex justify-center"
          >
            <button
              type="button"
              onClick={() => setLocation("/library")}
              className="inline-flex h-[45px] items-center gap-2 px-6 rounded-full text-sm font-semibold bg-foreground text-white shadow-[0_1px_1px_rgba(47,41,37,0.1),0_1px_1.5px_rgba(47,41,37,0.1)] hover:bg-foreground/90 hover:-translate-y-0.5 transition-all"
            >
              Explore the library
            </button>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
